import {isNil} from 'ramda';

import type {EagGridStore} from '../../../store/createEagGridStore';

type ScrollPosition = {element: HTMLElement; top: number; left: number};
type RowAnchor = {element: HTMLElement; offset: number};
type GridPosition = ScrollPosition & {autoHeight: boolean; anchor: RowAnchor | null};

const SCROLL_AREA_SELECTOR = '[data-eag-grid-scroll-area]';

const capturePosition = (element: HTMLElement): ScrollPosition => ({
  element,
  top: element.scrollTop,
  left: element.scrollLeft,
});

const captureAncestors = (surface: HTMLElement) => {
  const positions: ScrollPosition[] = [];
  let element = surface.parentElement;
  while (element) {
    positions.push(capturePosition(element));
    element = element.parentElement;
  }
  return positions;
};

const restorePosition = (position: ScrollPosition) => {
  if (
    position.element.isConnected &&
    (position.element.scrollTop !== position.top || position.element.scrollLeft !== position.left)
  ) {
    position.element.scrollTo({top: position.top, left: position.left, behavior: 'instant'});
  }
};

/** An auto-height grid scrolls with its page; translate the visible row into internal scroll. */
const captureRowAnchor = (element: HTMLElement, ancestors: ScrollPosition[]): RowAnchor | null => {
  const headerHeight = element.querySelector('thead')?.getBoundingClientRect().height ?? 0;
  const viewportTop = Math.max(
    element.getBoundingClientRect().top + headerHeight,
    0,
    ...ancestors
      .filter(({element: ancestor}) => /(auto|scroll)/.test(getComputedStyle(ancestor).overflowY))
      .map(({element: ancestor}) => ancestor.getBoundingClientRect().top + ancestor.clientTop)
  );
  const row = Array.from(element.querySelectorAll<HTMLElement>('tr[data-row-id]')).find(
    (candidate) => candidate.getBoundingClientRect().bottom > viewportTop
  );
  return row ? {element: row, offset: row.getBoundingClientRect().top - viewportTop} : null;
};

const captureGrid = (surface: HTMLElement, ancestors: ScrollPosition[]): GridPosition | null => {
  const element = surface.querySelector<HTMLElement>(SCROLL_AREA_SELECTOR);
  if (isNil(element)) {
    return null;
  }
  const autoHeight = element.dataset.autoHeight === 'true';
  return {
    ...capturePosition(element),
    autoHeight,
    anchor: autoHeight ? captureRowAnchor(element, ancestors) : null,
  };
};

const clampOffset = (offset: number, maximum: number) =>
  Math.min(Math.max(offset, 0), Math.max(maximum, 0));

/**
 * Owns transient scroll state for one surface. Capture precedes the store mutation;
 * restoration follows React's size measurement and any resulting ResizeObserver pass.
 */
export function preserveGridMaximizationScroll(surface: HTMLElement, store: EagGridStore) {
  let ancestors: ScrollPosition[] = [];
  let grid: GridPosition | null = null;
  let requested: ScrollPosition | null = null;
  let isMaximized = false;
  let isSettled = false;
  let frame: number | undefined;
  let settleFrame: number | undefined;

  const restore = () => {
    if (surface.dataset.eagGridMaximized !== isMaximized.toString()) {
      return;
    }
    if (grid && requested && grid.element.isConnected) {
      if (isMaximized && grid.anchor?.element.isConnected) {
        const headerHeight =
          grid.element.querySelector('thead')?.getBoundingClientRect().height ?? 0;
        requested.top =
          grid.element.scrollTop +
          grid.anchor.element.getBoundingClientRect().top -
          grid.element.getBoundingClientRect().top -
          headerHeight -
          grid.anchor.offset;
        grid.anchor = null;
      }
      restorePosition({
        ...requested,
        top: !isMaximized && grid.autoHeight ? grid.top : requested.top,
      });
    }
    if (!isMaximized) {
      ancestors.forEach(restorePosition);
    }
  };

  const cancelFrames = () => {
    if (!isNil(frame)) {
      cancelAnimationFrame(frame);
    }
    if (!isNil(settleFrame)) {
      cancelAnimationFrame(settleFrame);
    }
  };

  const observer = new ResizeObserver(() => scheduleRestore());
  const scheduleRestore = () => {
    cancelFrames();
    frame = requestAnimationFrame(() => {
      restore();
      // useAvailableSizePx may publish a second layout after the first measurement.
      settleFrame = requestAnimationFrame(() => {
        restore();
        isSettled = true;
        if (!isMaximized) {
          observer.disconnect();
          ancestors = [];
          grid = null;
          requested = null;
        }
      });
    });
  };

  const unsubscribeTransition = store.subscribeBeforeMaximizationChange((nextIsMaximized) => {
    if (nextIsMaximized) {
      // A quick re-open may precede the queued inline restoration.
      restore();
      ancestors = captureAncestors(surface);
      grid = captureGrid(surface, ancestors);
      requested = grid ? capturePosition(grid.element) : null;
      observer.observe(surface);
      if (grid) {
        observer.observe(grid.element);
      }
    }
    isMaximized = nextIsMaximized;
    isSettled = false;
    scheduleRestore();
  });

  const unsubscribeData = store.subscribe((state, previous) => {
    if (state.queryId !== previous.queryId || state.page !== previous.page) {
      // A different result set must never inherit a row offset from the old view.
      grid = null;
      requested = null;
    }
  });

  const onScroll = (event: Event) => {
    if (!isMaximized || !isSettled || !requested || event.target !== requested.element) {
      return;
    }
    const element = requested.element;
    // Resize-induced clamping is not navigation. Keep the unclamped offset until
    // a scroll moves away from it; track the axes independently.
    if (
      element.scrollTop !== clampOffset(requested.top, element.scrollHeight - element.clientHeight)
    ) {
      requested.top = element.scrollTop;
    }
    if (
      element.scrollLeft !== clampOffset(requested.left, element.scrollWidth - element.clientWidth)
    ) {
      requested.left = element.scrollLeft;
    }
  };
  surface.addEventListener('scroll', onScroll, true);

  return () => {
    unsubscribeTransition();
    unsubscribeData();
    surface.removeEventListener('scroll', onScroll, true);
    observer.disconnect();
    cancelFrames();
    // Capability removal keeps the inline surface mounted; navigation removes it.
    // Restore only the mounted surface, without changing the next page's scroll.
    if (ancestors.length > 0) {
      const positions = ancestors;
      const gridPosition =
        grid && requested ? {...requested, top: grid.autoHeight ? grid.top : requested.top} : null;
      requestAnimationFrame(() => {
        if (!surface.isConnected) {
          return;
        }
        if (gridPosition) {
          restorePosition(gridPosition);
        }
        positions.forEach(restorePosition);
      });
    }
    ancestors = [];
    grid = null;
    requested = null;
  };
}
